/**
 * Advanced Search Component
 * Provides UI for advanced property search with multiple filters
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useAdvancedSearch } from '../../agents/searchAgent';
import { useFavorites } from '../../agents/favoritesAgent';
import SearchPanel from '../ui/SearchPanel';
import FieldError from '../ui/FieldError';
import Badge from '../ui/Badge';
import OwnershipReviewedBadge from '../OwnershipReviewedBadge/OwnershipReviewedBadge';
import { isSold, isTourable } from '../../utils/listingStatus';
import { findRangeErrors, hasRangeErrors } from '../../utils/rangeValidation';
import './AdvancedSearchComponent.css';
import {
  isVacantLand,
  PROPERTY_TYPE_OPTIONS,
  propertyTypeLabel,
  VACANT_LAND_LABEL,
} from '../../utils/propertyKind';

/**
 * The filter keys a user actually sets. `sort_by`, `sort_order`, `limit` and
 * `offset` are seeded with defaults by useAdvancedSearch and would otherwise
 * make an untouched form look like it had four active filters.
 */
const FILTER_KEYS = [
  'city',
  'state',
  'zip_code',
  'property_type',
  'min_price',
  'max_price',
  'min_bedrooms',
  'max_bedrooms',
  'min_bathrooms',
  'max_bathrooms',
  'min_sqft',
  'max_sqft',
  'year_built_after',
  'year_built_before',
];

// Text filters clear to '', numeric ones to null - the shapes useAdvancedSearch
// seeds them with.
const TEXT_FILTER_KEYS = ['city', 'state', 'zip_code', 'property_type'];

const isFilterSet = (value) => value !== null && value !== undefined && value !== '';

const formatCompact = (value) => {
  const num = Number(value);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${Math.round(num / 1_000)}k`;
  return String(num);
};

export default function AdvancedSearchComponent({
  initialFilters,
  onViewHouse,
  onRequestTour,
  buyerId,
}) {
  const {
    filters,
    results,
    isSearching,
    error,
    updateFilter,
    executeSearch,
    resetFilters,
    nextPage,
    previousPage,
  } = useAdvancedSearch(initialFilters);

  const { addFavorite, removeFavorite, fetchFavorites, isFavorite } = useFavorites(buyerId);

  const searchPanelRef = useRef(null);

  // Inverted min/max ranges block the search (QA run 7 S3).
  const rangeErrors = useMemo(() => findRangeErrors(filters), [filters]);
  const rangeInvalid = hasRangeErrors(rangeErrors);

  // Props for an upper-bound input: flag it and point at its inline error.
  const rangeErrorProps = (name) => (rangeErrors[name]
    ? { 'aria-invalid': true, 'aria-describedby': `${name}-error` }
    : {});

  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((key) => isFilterSet(filters[key])).length,
    [filters]
  );

  const filterChips = useMemo(() => {
    const chips = [];
    if (filters.city) chips.push({ key: 'city', label: filters.city });
    if (filters.state) chips.push({ key: 'state', label: filters.state });
    if (filters.zip_code) chips.push({ key: 'zip_code', label: filters.zip_code });
    if (filters.property_type) {
      chips.push({ key: 'property_type', label: propertyTypeLabel(filters.property_type) });
    }
    if (isFilterSet(filters.min_price)) {
      chips.push({ key: 'min_price', label: `$${formatCompact(filters.min_price)}+` });
    }
    if (isFilterSet(filters.max_price)) {
      chips.push({ key: 'max_price', label: `<=$${formatCompact(filters.max_price)}` });
    }
    if (isFilterSet(filters.min_bedrooms)) {
      chips.push({ key: 'min_bedrooms', label: `${filters.min_bedrooms}+ bed` });
    }
    return chips.slice(0, 3);
  }, [filters]);

  const handleRemoveFilter = (key) => {
    updateFilter(key, TEXT_FILTER_KEYS.includes(key) ? '' : null);
  };



  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).some((k) => initialFilters[k] != null && initialFilters[k] !== '')) {
      executeSearch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (buyerId) {
      fetchFavorites();
    }
  }, [buyerId, fetchFavorites]);

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value);
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value ? parseFloat(value) : null);
  };

  const handlePropertyChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value ? parseInt(value, 10) : null);
  };

  const handleFloatChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value ? parseFloat(value) : null);
  };

  const handleSortChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Keep the panel open so the inline range errors stay in view.
    if (rangeInvalid) return;
    executeSearch();
    // Submitting collapses the panel so the results are what the user lands on.
    // Reset deliberately does not, so filters can be re-entered.
    searchPanelRef.current?.close();
  };

  const handleReset = () => {
    resetFilters();
  };

  const handleToggleFavorite = async (property) => {
    if (!buyerId || !property?.id) return;
    try {
      if (isFavorite(property.id)) {
        await removeFavorite(property.id);
      } else {
        await addFavorite(property.id, `Interested in property at ${property.address}`);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error toggling favorite from advanced search:', err);
    }
  };

  return (
    <div className="advanced-search-container">
      <h2 className="advanced-search-title">Advanced Property Search</h2>
      <p className="advanced-search-subtitle">Use filters below to quickly narrow homes by location, price, and features.</p>

      <SearchPanel
        ref={searchPanelRef}
        title="Filters"
        regionLabel="Advanced property search filters"
        idPrefix="advanced-search-panel"
        bodyLabel="Advanced search form"
        scrollBody={false}
        chips={filterChips}
        onChipDismiss={handleRemoveFilter}
        badgeCount={activeFilterCount}
      >
        {/* The form lives inside the panel body, never wrapping the bar: the
            panel's toggle and chip buttons must not be submit buttons. */}
        <form onSubmit={handleSearch} className="search-form">
        {/* Location Filters */}
        <fieldset className="filter-section">
          <legend>Location</legend>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                type="text"
                name="city"
                placeholder="e.g., Austin"
                value={filters.city}
                onChange={handleLocationChange}
                autoComplete="address-level2"
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="state">State</label>
              <input
                id="state"
                type="text"
                name="state"
                placeholder="e.g., TX"
                maxLength="2"
                value={filters.state}
                onChange={handleLocationChange}
                autoComplete="address-level1"
                autoCapitalize="characters"
                spellCheck={false}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="zip_code">Zip Code</label>
              <input
                id="zip_code"
                type="text"
                name="zip_code"
                placeholder="e.g., 78701"
                value={filters.zip_code}
                onChange={handleLocationChange}
                autoComplete="postal-code"
                inputMode="numeric"
                pattern="[0-9]*"
                className="filter-input"
              />
            </div>
          </div>
        </fieldset>

        {/* Property type (QA 2026-10-16: no way to search for land or condos only) */}
        <fieldset className="filter-section">
          <legend>Property Type</legend>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="property_type">Property type</label>
              <select
                id="property_type"
                name="property_type"
                value={filters.property_type || ''}
                onChange={handleLocationChange}
                className="filter-input"
              >
                <option value="">Any</option>
                {PROPERTY_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Price Filters */}
        <fieldset className="filter-section">
          <legend>Price Range</legend>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="min_price">Min Price</label>
              <input
                id="min_price"
                type="number"
                name="min_price"
                placeholder="0"
                value={filters.min_price || ''}
                onChange={handlePriceChange}
                inputMode="decimal"
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="max_price">Max Price</label>
              <input
                id="max_price"
                type="number"
                name="max_price"
                placeholder="1000000"
                value={filters.max_price || ''}
                onChange={handlePriceChange}
                inputMode="decimal"
                className="filter-input"
                {...rangeErrorProps('max_price')}
              />
              <FieldError id="max_price-error" message={rangeErrors.max_price} />
            </div>
          </div>
        </fieldset>

        {/* Property Features */}
        <fieldset className="filter-section">
          <legend>Property Features</legend>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="min_bedrooms">Min Bedrooms</label>
              <input
                id="min_bedrooms"
                type="number"
                name="min_bedrooms"
                min="0"
                placeholder="0"
                value={filters.min_bedrooms || ''}
                onChange={handlePropertyChange}
                inputMode="numeric"
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="max_bedrooms">Max Bedrooms</label>
              <input
                id="max_bedrooms"
                type="number"
                name="max_bedrooms"
                min="0"
                placeholder="10"
                value={filters.max_bedrooms || ''}
                onChange={handlePropertyChange}
                inputMode="numeric"
                className="filter-input"
                {...rangeErrorProps('max_bedrooms')}
              />
              <FieldError id="max_bedrooms-error" message={rangeErrors.max_bedrooms} />
            </div>

            <div className="filter-group">
              <label htmlFor="min_bathrooms">Min Bathrooms</label>
              <input
                id="min_bathrooms"
                type="number"
                name="min_bathrooms"
                min="0"
                step="0.5"
                placeholder="0"
                value={filters.min_bathrooms || ''}
                onChange={handleFloatChange}
                inputMode="decimal"
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="max_bathrooms">Max Bathrooms</label>
              <input
                id="max_bathrooms"
                type="number"
                name="max_bathrooms"
                min="0"
                step="0.5"
                placeholder="10"
                value={filters.max_bathrooms || ''}
                onChange={handleFloatChange}
                inputMode="decimal"
                className="filter-input"
                {...rangeErrorProps('max_bathrooms')}
              />
              <FieldError id="max_bathrooms-error" message={rangeErrors.max_bathrooms} />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="min_sqft">Min Sq Ft</label>
              <input
                id="min_sqft"
                type="number"
                name="min_sqft"
                placeholder="0"
                value={filters.min_sqft || ''}
                onChange={handlePriceChange}
                inputMode="numeric"
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="max_sqft">Max Sq Ft</label>
              <input
                id="max_sqft"
                type="number"
                name="max_sqft"
                placeholder="10000"
                value={filters.max_sqft || ''}
                onChange={handlePriceChange}
                inputMode="numeric"
                className="filter-input"
                {...rangeErrorProps('max_sqft')}
              />
              <FieldError id="max_sqft-error" message={rangeErrors.max_sqft} />
            </div>
          </div>
        </fieldset>

        {/* Sorting */}
        <fieldset className="filter-section">
          <legend>Sorting</legend>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="sort_by">Sort By</label>
              <select
                id="sort_by"
                name="sort_by"
                value={filters.sort_by}
                onChange={handleSortChange}
                className="filter-select"
              >
                <option value="price">Price</option>
                <option value="newest">Newest</option>
                <option value="address">Address</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="sort_order">Order</label>
              <select
                id="sort_order"
                name="sort_order"
                value={filters.sort_order}
                onChange={handleSortChange}
                className="filter-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </fieldset>

        {/* Action Buttons */}
        <div className="button-group">
          <button
            type="submit"
            disabled={isSearching || rangeInvalid}
            className="search-button"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="reset-button"
          >
            Reset Filters
          </button>
        </div>
        </form>
      </SearchPanel>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="results-container">
          <div className="results-header">
            <h3>Results</h3>
            <p className="results-count" aria-live="polite">
              Showing {(results.results || []).length} of {results.total_count} properties
            </p>
          </div>

          {(results.results || []).length > 0 ? (
            <>
              <div className="properties-grid">
                {(results.results || []).map((property) => (
                  <div key={property.id} className="property-card">
                    <div className="property-image-container">
                      {property.image_urls && property.image_urls.length > 0 ? (
                        <img
                          src={property.image_urls[0]}
                          alt={property.address}
                          className="property-image"
                        />
                      ) : (
                        <div className="property-no-image">No Image</div>
                      )}
                      {isSold(property.listing_status) && (
                        <span className="property-status-overlay">
                          <Badge variant="listing" value="sold" />
                        </span>
                      )}
                    </div>

                    <div className="property-content">
                      <h4 className="property-address">{property.address}</h4>
                      <p className="property-location">
                        {property.city}, {property.state} {property.zip_code}
                      </p>

                      {isVacantLand(property) ? (
                      <div className="property-features">
                        <span className="feature">{VACANT_LAND_LABEL}</span>
                      </div>
                      ) : (
                      <div className="property-features">
                        <span className="feature">
                          <strong>{property.bedrooms}</strong> Beds
                        </span>
                        <span className="feature">
                          <strong>{property.bathrooms}</strong> Baths
                        </span>
                        <span className="feature">
                          <strong>{property.square_feet?.toLocaleString()}</strong> Sq Ft
                        </span>
                      </div>
                      )}

                      <p className="property-price">
                        ${property.price?.toLocaleString() || 'N/A'}
                      </p>

                      {property.ownership_documents_reviewed === true && (
                        <div className="property-trust-row">
                          <OwnershipReviewedBadge
                            reviewed
                            reviewedMonth={property.ownership_reviewed_month}
                            size="sm"
                          />
                        </div>
                      )}

                      {property.description && (
                        <p className="property-description">{property.description}</p>
                      )}

                      <div className="property-card-actions">
                        <button
                          type="button"
                          className={`property-action-btn property-action-btn--favorite ${isFavorite(property.id) ? 'is-active' : ''}`}
                          onClick={() => handleToggleFavorite(property)}
                          aria-label={isFavorite(property.id) ? 'Remove from favorites' : 'Add to favorites'}
                          disabled={!buyerId}
                          title={!buyerId ? 'Sign in as a buyer to favorite listings' : undefined}
                        >
                          {isFavorite(property.id) ? '❤️ Favorite' : '🤍 Favorite'}
                        </button>
                        <button
                          type="button"
                          className="property-action-btn property-action-btn--view"
                          onClick={() => onViewHouse?.(property.id)}
                          disabled={!onViewHouse}
                        >
                          👁 View
                        </button>
                        {/* Sold homes stay searchable as comps but take no
                            tours (TOURABLE_LISTING_STATUSES). */}
                        {isTourable(property.listing_status) && (
                          <button
                            type="button"
                            className="property-action-btn property-action-btn--tour"
                            onClick={() => onRequestTour?.(property.id)}
                            disabled={!onRequestTour}
                          >
                            🎫 Tour
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="pagination-controls">
                <button
                  onClick={previousPage}
                  disabled={filters.offset === 0 || isSearching}
                  className="pagination-button"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {Math.floor(filters.offset / filters.limit) + 1}
                </span>
                <button
                  onClick={nextPage}
                  disabled={(results.results || []).length < filters.limit || isSearching}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <div className="no-results">
              <p>No properties found matching your search criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
